import {
    Check,
    Circle,
    Clear,
    DeleteForeverOutlined,
    EditOutlined,
    KeyboardArrowDown,
    KeyboardArrowUp,
    LegendToggleOutlined,
    PlayArrow,
} from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import gql from "graphql-tag";
import { useState } from "react";
import { useMutation } from "react-apollo";
import { toast } from "react-toastify";
import styled from "styled-components";
import { GetDatacenter_datacenter_tla_deviceContexts } from "../../views/App/__generated__/GetDatacenter";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import {
    RunBootstrapsDeviceContextMutation,
    RunBootstrapsDeviceContextMutationVariables,
} from "./__generated__/RunBootstrapsDeviceContextMutation";
import ConfirmRunBootstrapsDialog from "./ConfirmRunBootstrapsDialog";
import TLADeviceContextSdiInputs from "./TLADatacenterDeviceContextSdiInputs";
import { navigate } from "@reach/router";
import { THEO_ID } from "../../views/App/AppRoot";

const Content = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    padding: 10px 14px;
    border: 1px solid ${(props) => props.theme.lightGrayBottom};
    align-items: center;
    width: 100%;
`;

const Main = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    cursor: pointer;
`;

const Title = styled.div`
    font-size: 14px;
`;

const Description = styled.div`
    font-size: 13px;
    color: ${(props) => props.theme.gray};
`;

const Details = styled.div`
    border-radius: 8px;
    background-color: ${(props) => props.theme.grayBackground};
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: 4px;
    font-size: 13px;
    padding: 10px 14px;
    border: 1px solid ${(props) => props.theme.lightGrayBottom};
`;

const Group = styled.div`
    margin-bottom: 8px;
`;

interface Props {
    datacenterId: string;
    deviceContext: GetDatacenter_datacenter_tla_deviceContexts;
    onEditClick: () => void;
    onDeleteClick: () => void;
}

export const RUN_BOOTSTRAPS_DEVICE_CONTEXT_MUTATION = gql`
    mutation RunBootstrapsDeviceContextMutation($input: RunBootstrapsInput!) {
        runDeviceContextBootstraps(input: $input)
    }
`;

export default function TLADatacenterDeviceContextsItem({
    deviceContext,
    datacenterId,
    onDeleteClick,
    onEditClick,
}: Props): JSX.Element {
    const [open, setOpen] = useState<boolean>(false);

    const [openRunBootstrapsDialog, setOpenRunBootstrapsDialog] = useState<boolean>(false);

    const {
        capacity,
        description,
        id,
        labels,
        name,
        privateId,
        serial,
        isCluster,
        joinToken,
        override,
        privateIp,
        state,
        sdiInputs,
    } = deviceContext;

    const [runBootstrapsMutation] = useMutation<
        RunBootstrapsDeviceContextMutation,
        RunBootstrapsDeviceContextMutationVariables
    >(RUN_BOOTSTRAPS_DEVICE_CONTEXT_MUTATION);

    async function runBootstraps(): Promise<void> {
        await runBootstrapsMutation({ variables: { input: { id } } });
        toast.success("Upgrade bootstraps", TOAST_SETTINGS);
        setOpenRunBootstrapsDialog(false);
    }

    return (
        <>
            <Content>
                <Main onClick={() => setOpen(!open)}>
                    <div className="flex flex-row gap-2">
                        <div>
                            <Tooltip title={state}>
                                <Circle
                                    style={{
                                        fontSize: "12px",
                                        color: state === "creating" ? "orange" : state === "created" ? "green" : "gray",
                                    }}
                                />
                            </Tooltip>
                        </div>
                        <div>
                            <Title>{name}</Title>
                            <Description>{description}</Description>
                        </div>
                    </div>
                    <div>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/${THEO_ID}/monitoring/${id}`);
                            }}
                        >
                            <LegendToggleOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditClick();
                            }}
                        >
                            <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClick();
                            }}
                        >
                            {<DeleteForeverOutlined fontSize="small" />}
                        </IconButton>
                        <IconButton size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUp color="primary" /> : <KeyboardArrowDown color="primary" />}
                        </IconButton>
                    </div>
                </Main>
                {open && (
                    <Details>
                        <Group>
                            <strong>ID</strong>
                            <div>{id}</div>
                        </Group>
                        <Group>
                            <strong>Name</strong>
                            <div>{name}</div>
                        </Group>
                        <Group>
                            <strong>Description</strong>
                            <div>{description}</div>
                        </Group>
                        <Group>
                            <strong>Capacity</strong>
                            <div>{capacity}</div>
                        </Group>
                        <Group>
                            <strong>Serial</strong>
                            <div>{serial}</div>
                        </Group>
                        <Group>
                            <strong>Private ID</strong>
                            <div>{privateId}</div>
                        </Group>
                        {joinToken && (
                            <Group>
                                <strong>Private IP</strong>
                                <div>{privateIp}</div>
                            </Group>
                        )}
                        {joinToken && (
                            <Group>
                                <strong>Join token</strong>
                                <div>{joinToken}</div>
                            </Group>
                        )}
                        <Group>
                            <strong>Is cluster?</strong>
                            <div>{isCluster ? <Check color="success" /> : <Clear color="error" />}</div>
                        </Group>

                        {override && (
                            <Group>
                                <strong>Override</strong>
                                <div>{JSON.stringify(JSON.parse(override), undefined, 2)}</div>
                            </Group>
                        )}
                        <Group>
                            <strong>Labels</strong>
                            <ul style={{ margin: 0, padding: 0, paddingLeft: "28px" }}>
                                {labels.map((label, i) => (
                                    <li key={`label-${i}`}>{label}</li>
                                ))}
                            </ul>
                        </Group>

                        <Group>
                            <strong>Grafana dashboards</strong>
                            <div style={{ display: "flex", flexDirection: "row", gap: "10px", marginTop: "4px" }}>
                                <Button
                                    size="small"
                                    color="secondary"
                                    onClick={() =>
                                        window.open(
                                            `https://grafana.eu-west-3.cuteanteater.com/d/pMEd7m0Mz/cadvisor-exporter?orgId=1&var-job=cadvisor&var-instance=${id}`,
                                        )
                                    }
                                >
                                    Cadvisor exporter
                                </Button>
                                <Button
                                    size="small"
                                    color="secondary"
                                    onClick={() =>
                                        window.open(
                                            `https://grafana.eu-west-3.cuteanteater.com/d/rYdddlPWk/full-node-exporter?orgId=1&refresh=1m&var-datasource=default&var-job=node-exporter&var-node=${id}&var-diskdevices=%5Ba-z%5D%2B%7Cnvme%5B0-9%5D%2Bn%5B0-9%5D%2B%7Cmmcblk%5B0-9%5D%2B&from=now-5m&to=now`,
                                        )
                                    }
                                >
                                    Full node exporter
                                </Button>
                            </div>
                        </Group>
                        <Group>
                            <TLADeviceContextSdiInputs
                                deviceContextId={deviceContext.serial}
                                sdiInputs={sdiInputs ?? []}
                            />
                        </Group>
                        <Group>
                            <div className="bg-white rounded-md px-2 py-3 flex flex-col gap-1 border-gray border-[1px]">
                                <div className="w-full flex flex-row justify-between items-center mb-4">
                                    <strong>Bootstraps</strong>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<PlayArrow />}
                                        onClick={() => {
                                            setOpenRunBootstrapsDialog(true);
                                        }}
                                    >
                                        Run
                                    </Button>
                                </div>
                            </div>
                        </Group>
                    </Details>
                )}
            </Content>
            <ConfirmRunBootstrapsDialog
                open={openRunBootstrapsDialog}
                onClose={() => setOpenRunBootstrapsDialog(false)}
                type="datacenter"
                onConfirm={() => runBootstraps()}
            />
        </>
    );
}
